import React, { lazy, Suspense, useState, useMemo, useRef, useCallback, useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { motion, useAnimation } from "framer-motion";
import { gsap } from "gsap";
import { ArrowRight } from "lucide-react";

import CursorAnimation from "../../../container/Header/cursor";
import dummyJobs from "../../../db/demoJobs.json";

// Import the original Header CSS so all demo styling is preserved exactly
import "../../../container/Header/Header.css";

// The demo swipe card reuses the full dashboard JobCard (a large module).
// Lazy-load it so the hero text paints instantly; the card streams into its
// fixed-size container a moment later.
const JobCard = lazy(() => import("../../../pages/Dashboard/Apply"));

const getRandomDirection = () => (Math.random() <= 0.5 ? "left" : "right");

const cardVariants = {
  center: {
    x: 0,
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  left: {
    x: -2000,
    y: -150,
    opacity: 0,
    transition: { duration: 0.4, ease: "easeInOut" },
  },
  right: {
    x: 2000,
    y: -150,
    opacity: 0,
    transition: { duration: 0.4, ease: "easeInOut" },
  },
};

const CinematicHero = () => {
  const isWide = useMediaQuery("(min-width:1101px)", { noSsr: true });
  const heroRef = useRef(null);

  // ─── GSAP text entrance ───
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".gsap-hero-el",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          stagger: 0.08,
          ease: "power3.out",
          delay: 0.1,
        }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // ─── Swiping demo (identical to Header.js) ───
  const jobs = useMemo(() => {
    return dummyJobs.map((job) => ({
      ...job,
      jobPosted:
        Date.now() - 1000 * 60 * 60 * 24 * Math.floor(Math.random() * 10),
    }));
  }, []);

  const controls = useAnimation();
  const containerRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentDirection, setCurrentDirection] = useState("left");
  const [currentAction, setCurrentAction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const simulatedEvent = useMemo(() => ({
    direction: currentDirection,
    action: currentAction,
    animationControl: controls,
    index: currentIndex,
  }), [currentDirection, currentAction, controls, currentIndex]);

  const incrementSimulatedIndex = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % jobs.length);
  }, [jobs.length]);

  const resetSimulatedEvent = useCallback(() => {
    setCurrentAction(null);
  }, []);

  const startNewCycle = useCallback(() => {
    setCurrentAction(null);
    setCurrentDirection(getRandomDirection());
  }, []);

  const currentQueryParams = window.location.search;

  return (
    <section className="cinematic-hero" ref={heroRef} id="home">
      <div className="cinematic-hero__content">
        {/* ─── Text side ─── */}
        <div className="cinematic-hero__text">
          <div className="cinematic-hero__badge gsap-hero-el">
            AI-Powered Auto-Apply
          </div>

          <h1 className="cinematic-hero__title gsap-hero-el">
            Land your next interview
            <span className="cinematic-hero__title-drama">
              on autopilot.
            </span>
          </h1>

          <p className="cinematic-hero__subtitle gsap-hero-el">
            Set your preferences once. Our AI matches you to thousands of roles
            and auto-applies while you focus on what matters — interviewing.
          </p>

          <div className="cinematic-hero__actions gsap-hero-el">
            <a
              href={`/signup${currentQueryParams}`}
              className="cinematic-btn cinematic-btn--primary"
            >
              <span className="btn-bg" />
              <span>Get Started — It&apos;s Free</span>
              <ArrowRight size={18} style={{ position: "relative", zIndex: 1 }} />
            </a>
            <a href="/#features" className="cinematic-btn cinematic-btn--secondary">
              <span>Learn More</span>
            </a>
          </div>
        </div>

        {/* ─── Swiping demo — uses original Header structure ─── */}
        <div className="hero-demo-section">
          <div className="app__header-demo">
            <div className="demo-recessed-wrapper">
              <div className="demo-container">
                <Suspense fallback={null}>
                {[0, 1].map((offset) => {
                  const index =
                    (simulatedEvent.index + offset) % jobs.length;
                  const jobData = jobs[index];
                  const isTopCard = offset === 0;

                  return (
                    <motion.div
                      key={jobData.jobUrl || `card-${offset}`}
                      animate={isTopCard ? controls : "center"}
                      initial="center"
                      variants={cardVariants}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        left: 0,
                        zIndex: 1 - offset,
                      }}
                    >
                      <Box
                        className="demo-card-container"
                        ref={isTopCard ? containerRef : null}
                      >
                        <JobCard
                          jobData={jobData}
                          jobCardHeight="100%"
                          simulatedEvent={isTopCard ? simulatedEvent : null}
                          incrementSimulatedIndex={incrementSimulatedIndex}
                          resetSimulatedEvent={resetSimulatedEvent}
                          startNewCycle={startNewCycle}
                          jobTypeChipPosition="static"
                          enableModalOnSmallScreen={true}
                          onModalStateChange={setIsModalOpen}
                          isDemo={true}
                        />
                        {isTopCard && (
                          <CursorAnimation
                            key={`cursor-${currentIndex}-${currentDirection}`}
                            direction={currentDirection}
                            containerRef={containerRef}
                            sendEvent={(action) => {
                              setCurrentAction(action);
                            }}
                            isPaused={isModalOpen}
                          />
                        )}
                      </Box>
                    </motion.div>
                  );
                })}
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CinematicHero;
